import { Combobox } from '@headlessui/react';
import { EMPTY_ARRAY, isNotEmptyArray, isNotEmptyString } from '@weft/utils';
import { Input } from 'apps/app/src/components/design/Input';
import cn from 'classnames';
import React, { useState, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

export type MultiselectProps<Item> = {  
  items: Item[];
  selectedItems: Item[];
  onItemsChanged: (items: Item[]) => void;
  getId: (item: Item) => string;
  getDisplayValue: (item: Item) => string;
  label?: string;
  labelClassName?: string;
  inputClassName?: string;
  containerClassName?: string;
  activeOptionClassName?: string;
  optionClassName?: string;
  selectedOptionClassName?: string;
  multiselectDisabled?: boolean;
  closeOnOptionClick?: boolean;
} & React.HTMLAttributes<HTMLInputElement>;

export function Multiselect<Item>({
  items,
  selectedItems,
  placeholder,
  label,
  containerClassName,
  inputClassName: externalInputClassName,
  labelClassName,
  activeOptionClassName: externalActiveOptionClassName,
  optionClassName: externalOptionClassName,
  selectedOptionClassName: externalSelectedOptionClassName,
  onItemsChanged,
  getId,
  getDisplayValue,
  multiselectDisabled,
  closeOnOptionClick = true,
  ...inputProps
}: MultiselectProps<Item>) {
  const [filterText, setFilterText] = useState('');    

  const filteredItems = useMemo(() => 
    isNotEmptyArray(items) 
      ? items.filter(item => getDisplayValue(item).toLowerCase().includes(filterText.toLowerCase()))
      : EMPTY_ARRAY
  , [items, filterText],
  );

  const optionClassName = twMerge('cursor-pointer px-6.5', externalOptionClassName);
  const activeOptionClassName = twMerge('bg-light-gray', externalActiveOptionClassName);
  const selectedOptionClassName = twMerge('font-semibold', externalSelectedOptionClassName);  

  return (
    <div
      className={containerClassName}
    >
      {isNotEmptyString(label) && (
        <div className={labelClassName}>
          {label}
        </div>
      )}      
      <Combobox
        
        multiple
        value={selectedItems}
        onChange={onItemsChanged}
        as="div"
        className="relative"
        disabled={multiselectDisabled}
      >
        <Combobox.Button className="w-full">
          <Combobox.Input
            className={({ open }) => cn(
              externalInputClassName,
              { 'cursor-pointer': !open },
              { 'opacity-50 cursor-default' :multiselectDisabled },
            )}
            onChange={e => setFilterText(e.target.value)}
            placeholder={placeholder}
            displayValue={() => filterText}
            as={Input}
            swallowClicks={false}
            debounce={false}
            autoComplete='off'
            defaultValue={filterText}
            {...inputProps}
          />
        </Combobox.Button>
        {isNotEmptyArray(filteredItems) && (
          <Combobox.Options className="absolute z-10 max-h-48 w-full overflow-y-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm">
            {
            filteredItems.map(item => (
              <Combobox.Option
                key={getId(item)}
                placeholder={placeholder}
                value={item}
                className={({ active, selected }) => cn(
                  optionClassName,
                  { [activeOptionClassName]: active },
                  { [selectedOptionClassName]: selected },
                  { '!p-0' : closeOnOptionClick },
                )}
              >
                {closeOnOptionClick ? (
                  <Combobox.Button className="px-6.5 w-full py-2 text-left">
                    {getDisplayValue(item)}
                  </Combobox.Button>
                ) : getDisplayValue(item)}
              </Combobox.Option>
            ))
          }
          </Combobox.Options>
        )}
      </Combobox>
    </div>
  );
}